import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { REVIEW_STATUS_LABEL, label } from '../lib/labels.js';
import { date, dateTime, num } from '../lib/format.js';
import {
  Confirm,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';

interface BatchRow {
  race_id: string;
  race_name: string;
  status: string;
  reason: string | null;
  decided_at: string | null;
  decided_by_email: string | null;
  date_end: string | null;
  timezone: string;
  review_policy: string;
  trips: number;
  sent: number;
  excluded: Array<{ orderCode: string; reason: string }> | null;
}

const EXCLUDE_REASON: Record<string, string> = {
  tickets_not_delivered: 'nedoručené vstupenky',
  order_inactive: 'storno nebo vrácení',
  open_claim: 'otevřená reklamace',
  manual: 'ručně vyřazeno',
};

export function Reviews({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [holdReason, setHoldReason] = useState<Record<string, string>>({});
  const [skipping, setSkipping] = useState<BatchRow | null>(null);

  const query = useQuery<{ items: BatchRow[] }>({
    queryKey: ['reviews'],
    queryFn: () => api.get('/api/reviews'),
  });

  const decide = useMutation({
    mutationFn: (input: { raceId: string; decision: string; reason?: string }) =>
      api.post(`/api/reviews/${encodeURIComponent(input.raceId)}/decide`, {
        decision: input.decision,
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <LoadingState what="dávky hodnocení" />;
  if (query.isError)
    return (
      <ErrorState
        title="Dávky hodnocení se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );

  const canDecide = rights.can('decide_review_batch');

  return (
    <>
      <PageHeader
        title="Hodnocení"
        description="Po skončení závodu vznikne dávka žádostí o hodnocení. Dokud ji někdo neschválí, žádost neodejde; objednávky s reklamací se neposílají vůbec."
      />

      {error ? (
        <ErrorState title="Rozhodnutí se nepovedlo uložit" error={new Error(error)} />
      ) : null}
      {!canDecide ? (
        <p className="hint">
          Rozhodovat o dávkách smí péče o zákazníky a administrátor. Vy je vidíte jen ke čtení.
        </p>
      ) : null}

      <Panel title="Dávky po závodech">
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Závod</th>
                <th>Konec</th>
                <th>Stav</th>
                <th className="n">Příjemců</th>
                <th className="n">Odesláno H01</th>
                <th>Vyřazené</th>
                <th>Rozhodnutí</th>
                {canDecide ? <th>Akce</th> : null}
              </tr>
            </thead>
            <tbody>
              {query.data?.items.map((b) => (
                <tr key={b.race_id} data-testid={`review-${b.race_id}`}>
                  <td>
                    <a href={hrefFor('races', b.race_id)}>{b.race_name}</a>
                    <div className="muted">
                      <code>{b.race_id}</code>
                    </div>
                  </td>
                  <td>{date(b.date_end, b.timezone)}</td>
                  <td data-testid={`review-status-${b.race_id}`}>
                    <StatusBadge
                      tone={
                        b.status === 'approved' ? 'ok' : b.status === 'pending' ? 'warn' : 'neu'
                      }
                      icon={
                        b.status === 'approved'
                          ? 'check'
                          : b.status === 'pending'
                            ? 'clock'
                            : 'info'
                      }
                    >
                      {label(REVIEW_STATUS_LABEL, b.status)}
                    </StatusBadge>
                  </td>
                  <td className="n">{num(b.trips)}</td>
                  <td className="n">{num(b.sent)}</td>
                  <td className="muted">
                    {(b.excluded ?? []).length === 0
                      ? '—'
                      : (b.excluded ?? [])
                          .map((e) => `${e.orderCode} (${label(EXCLUDE_REASON, e.reason)})`)
                          .join(', ')}
                  </td>
                  <td>
                    {b.decided_at ? (
                      <>
                        {dateTime(b.decided_at, me.defaultTimezone)}
                        <div className="muted">{b.decided_by_email ?? ''}</div>
                        {b.reason ? <div className="muted">{b.reason}</div> : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  {canDecide ? (
                    <td>
                      {b.status === 'pending' ? (
                        <div className="row">
                          <Confirm
                            label="Schválit"
                            question={`Schválit dávku hodnocení pro ${b.race_name}? H01 pak odejde příjemcům, kteří nejsou vyřazení.`}
                            confirmLabel="Schválit"
                            onConfirm={() =>
                              decide.mutate({ raceId: b.race_id, decision: 'approved' })
                            }
                          />
                          <input
                            type="text"
                            placeholder="Důvod pozdržení"
                            aria-label={`Důvod pozdržení ${b.race_id}`}
                            value={holdReason[b.race_id] ?? ''}
                            onChange={(e) =>
                              setHoldReason({ ...holdReason, [b.race_id]: e.target.value })
                            }
                          />
                          <button
                            className="btn"
                            disabled={!(holdReason[b.race_id] ?? '').trim()}
                            onClick={() =>
                              decide.mutate({
                                raceId: b.race_id,
                                decision: 'held',
                                reason: holdReason[b.race_id],
                              })
                            }
                          >
                            Pozdržet
                          </button>
                          <button className="btn dng" onClick={() => setSkipping(b)}>
                            Přeskočit
                          </button>
                        </div>
                      ) : (
                        <span className="muted">rozhodnuto</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {query.data && query.data.items.length === 0 ? (
          <EmptyState title="Zatím žádná dávka hodnocení">
            Dávka vznikne po skončení prvního závodu. Až se tak stane, objeví se tady ke schválení.
          </EmptyState>
        ) : null}
        <p className="note s2a-mt">
          <b>Pozdržet smí jen provozní důvod</b> — ne nepohodlný výsledek. Důvod je povinný a zapíše
          se do auditu.
        </p>
      </Panel>
      <ConfirmDialog
        open={skipping !== null}
        title="Přeskočit hodnocení?"
        danger
        confirmLabel="Přeskočit hodnocení"
        onCancel={() => setSkipping(null)}
        onConfirm={() => {
          if (skipping) decide.mutate({ raceId: skipping.race_id, decision: 'skipped' });
          setSkipping(null);
        }}
      >
        Přeskočit hodnocení pro {skipping?.race_name}? Žádost o hodnocení už pak nikdy neodejde.
      </ConfirmDialog>
    </>
  );
}
